import { formatMinor, type AuditEventRecord } from '@hvac/contracts';

/**
 * Turning the trail into something a person can read.
 *
 * The trail holds two kinds of entry: sentences the API wrote ("Signed off 147
 * records") and changes the database recorded on its own, with every field
 * that moved. Both carry who did it; the database's also say what kind of
 * hand (a person, the system, or automatic filing).
 */

export type Source = 'person' | 'automatic' | 'system';

export function sourceOf(event: AuditEventRecord): Source {
  const recorded = event.metadata['source'];
  if (recorded === 'automatic' || recorded === 'system' || recorded === 'person') return recorded;
  return event.actorType === 'user' ? 'person' : 'system';
}

export interface EventGroup {
  readonly key: string;
  readonly events: readonly AuditEventRecord[];
}

/**
 * Consecutive entries that are the same act by the same hand collapse into
 * one: forty uploads in a row are one line, "40 × uploaded a file", not forty.
 */
export function groupEvents(events: readonly AuditEventRecord[]): EventGroup[] {
  const groups: { key: string; events: AuditEventRecord[] }[] = [];
  for (const event of events) {
    const key = `${event.occurredAt.slice(0, 10)}|${event.action}|${event.actorId ?? ''}|${sourceOf(event)}|${event.summary}`;
    const last = groups[groups.length - 1];
    if (last?.key === key) last.events.push(event);
    else groups.push({ key, events: [event] });
  }
  return groups;
}

export interface ChangeLine {
  readonly field: string;
  readonly from: string;
  readonly to: string;
}

const label = (field: string): string =>
  field
    .replace(/_minor$/, '')
    .replace(/_id$/, '')
    .replaceAll('_', ' ');

function show(field: string, value: unknown): string {
  if (value === null || value === undefined || value === '') return 'nothing';
  if (field.endsWith('_minor') && (typeof value === 'number' || typeof value === 'string')) {
    return formatMinor(String(value));
  }
  // An id says only that the link changed; the number itself means nothing to a reader.
  if (field.endsWith('_id')) return 'set';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

/** The fields an update moved, from what to what, in words. */
export function describeChanges(event: AuditEventRecord): ChangeLine[] {
  const changes = event.metadata['changes'];
  if (changes === null || typeof changes !== 'object') return [];
  return Object.entries(changes as Record<string, { from?: unknown; to?: unknown }>)
    .map(([field, change]) => ({
      field: label(field),
      from: show(field, change.from),
      to: show(field, change.to),
    }))
    .sort((a, b) => a.field.localeCompare(b.field));
}
